import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function ensureAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .eq("role", "admin")
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

/* -------- Ideas backlog -------- */

export const listIdeas = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { status?: string } | undefined) =>
    z.object({ status: z.string().optional() }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    let q = context.supabase
      .from("article_ideas")
      .select("*")
      .order("discovered_at", { ascending: false })
      .limit(200);
    if (data.status) q = q.eq("status", data.status as any);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const decideIdeas = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        ids: z.array(z.string().uuid()).min(1).max(50),
        decision: z.enum(["approved", "rejected", "archived"]),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("article_ideas")
      .update({
        status: data.decision,
        decided_at: new Date().toISOString(),
        decided_by: context.userId,
      })
      .in("id", data.ids);
    if (error) throw new Error(error.message);
    return { ok: true, count: data.ids.length };
  });

export const sendIdeasToDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ ids: z.array(z.string().uuid()).min(1).max(20) }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { enqueueIdeasForDrafting } = await import("./editorial.server");
    return await enqueueIdeasForDrafting(data.ids, context.userId);
  });

/* -------- Discovery -------- */

export const triggerDiscovery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ max: z.number().int().min(1).max(30).default(15) }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { discoverAndStoreIdeas, refillEditorialQueue } = await import("./editorial.server");
    const discovery = await discoverAndStoreIdeas({ maxCandidates: data.max, autoQueueBreaking: true, timeBudgetMs: 45_000 });
    const refill = await refillEditorialQueue({ bufferDays: 3, maxBuffer: 18, triggeredBy: context.userId });
    return { ...discovery, refill };
  });

export const triggerAutopilotRefill = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { refillEditorialQueue } = await import("./editorial.server");
    return await refillEditorialQueue({ bufferDays: 3, maxBuffer: 18, triggeredBy: context.userId });
  });

export const resetStuckJobs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { resetStuckJobs: reset } = await import("./editorial.server");
    return await reset(15);
  });

/* -------- Schedule settings -------- */

export const getScheduleSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("publish_schedule_settings")
      .select("*")
      .eq("id", 1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  });

export const updateScheduleSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        articles_per_day: z.number().int().min(0).max(10),
        publish_times: z.array(z.string()).min(1).max(6),
        timezone: z.string().min(2).max(64),
        auto_publish: z.boolean(),
        min_quality_score: z.number().int().min(0).max(100),
        tolerance_floor: z.number().int().min(0).max(25).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    // NOTE: boost_articles_per_day / boost_until are intentionally NOT written here.
    // Boost changes go through updateBoostSettings so an unrelated "Save settings"
    // click can never resurrect a stale boost value from form state.
    const update: {
      articles_per_day: number;
      publish_times: string[];
      timezone: string;
      auto_publish: boolean;
      min_quality_score: number;
      tolerance_floor?: number;
    } = {
      articles_per_day: data.articles_per_day,
      publish_times: data.publish_times,
      timezone: data.timezone,
      auto_publish: data.auto_publish,
      min_quality_score: data.min_quality_score,
    };
    if (data.tolerance_floor !== undefined) update.tolerance_floor = data.tolerance_floor;
    const { error } = await context.supabase
      .from("publish_schedule_settings")
      .update(update as any)
      .eq("id", 1);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updateBoostSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        boost_articles_per_day: z.number().int().min(0).max(50).nullable(),
        boost_until: z.string().nullable(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const sb = context.supabase;
    const { data: prev } = await sb
      .from("publish_schedule_settings")
      .select("boost_articles_per_day, boost_until")
      .eq("id", 1)
      .maybeSingle();
    const { error } = await sb
      .from("publish_schedule_settings")
      .update({
        boost_articles_per_day: data.boost_articles_per_day,
        boost_until: data.boost_until,
      } as any)
      .eq("id", 1);
    if (error) throw new Error(error.message);
    // Best-effort audit log; do not fail the request if it can't insert.
    await sb.from("publish_setting_changes" as any).insert({
      changed_by: context.userId,
      field: "boost",
      old_value: {
        boost_articles_per_day: (prev as any)?.boost_articles_per_day ?? null,
        boost_until: (prev as any)?.boost_until ?? null,
      },
      new_value: {
        boost_articles_per_day: data.boost_articles_per_day,
        boost_until: data.boost_until,
      },
    });
    return { ok: true };
  });

export const listBoostChanges = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("publish_setting_changes" as any)
      .select("*")
      .eq("field", "boost")
      .order("changed_at", { ascending: false })
      .limit(10);
    if (error) throw new Error(error.message);
    return (data ?? []) as any[];
  });


export const listPublishDecisions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("publish_decisions" as any)
      .select("*")
      .order("decided_at", { ascending: false })
      .limit(20);
    if (error) throw new Error(error.message);
    return (data ?? []) as any[];
  });

/* -------- Manual worker triggers -------- */

export const processNextEditorialTask = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ kind: z.enum(["draft", "edit"]).optional() }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { processOneEditorialTask } = await import("./editorial.server");
    return await processOneEditorialTask(data.kind);
  });

export const runPublishDue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { publishDueArticles } = await import("./editorial.server");
    return await publishDueArticles();
  });

/* -------- Ready / pipeline view -------- */

export const listReadyArticles = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("articles")
      .select(
        "id, slug, title, status, quality_score, editor_pass_completed_at, updated_at, focus_keyword",
      )
      .in("status", ["draft", "ready_for_review", "scheduled"])
      .order("updated_at", { ascending: false })
      .limit(80);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getPipelineSnapshot = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { data: settings } = await context.supabase
      .from("publish_schedule_settings")
      .select("articles_per_day, boost_articles_per_day, boost_until, min_quality_score, tolerance_floor")
      .eq("id", 1)
      .maybeSingle();

    const toleranceMin = Math.max(
      0,
      Number((settings as any)?.min_quality_score ?? 70) -
        Number((settings as any)?.tolerance_floor ?? 15),
    );
    const boostUntil = (settings as any)?.boost_until
      ? new Date((settings as any).boost_until).getTime()
      : 0;
    const boostActive =
      Number((settings as any)?.boost_articles_per_day ?? 0) > 0 && boostUntil > Date.now();
    const effectiveRate = boostActive
      ? Number((settings as any)?.boost_articles_per_day)
      : Number((settings as any)?.articles_per_day ?? 2);

    const [ready, tolerance, pending, running, latestDecision] = await Promise.all([
      context.supabase
        .from("articles")
        .select("id", { count: "exact", head: true })
        .in("status", ["ready_for_review", "scheduled"]),
      context.supabase
        .from("articles")
        .select("id", { count: "exact", head: true })
        .eq("status", "draft")
        .not("editor_pass_completed_at", "is", null)
        .gte("quality_score", toleranceMin),
      context.supabase
        .from("article_generation_tasks")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      context.supabase
        .from("article_generation_tasks")
        .select("id", { count: "exact", head: true })
        .eq("status", "running"),
      context.supabase
        .from("publish_decisions" as any)
        .select("decided_at, reason, tier_fired, candidates_considered, target_today, published_today, behind_schedule")
        .order("decided_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    return {
      effectiveRate,
      boostActive,
      toleranceMin,
      readyCount: ready.count ?? 0,
      toleranceDraftCount: tolerance.count ?? 0,
      pendingTaskCount: pending.count ?? 0,
      runningTaskCount: running.count ?? 0,
      latestDecision: (latestDecision.data as any) ?? null,
    };
  });

/* -------- Backfill -------- */

export const runBackfillDiscovery = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      lookbackDays: z.number().int().min(7).max(180).default(60),
      maxCandidates: z.number().int().min(20).max(300).default(160),
    }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { discoverAndStoreIdeas } = await import("./editorial.server");
    return await discoverAndStoreIdeas({
      maxCandidates: data.maxCandidates,
      autoQueueBreaking: false,
      lookbackDays: data.lookbackDays,
      archiveSweep: true,
      timeBudgetMs: 50_000,
      concurrency: 6,
    });
  });

export const runBackfillApprove = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ targetCount: z.number().int().min(1).max(300).default(120) }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { autoApproveTopIdeas } = await import("./editorial.server");
    return await autoApproveTopIdeas(data.targetCount);
  });

export const runBackfillDrain = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      maxTasks: z.number().int().min(1).max(50).default(20),
      concurrency: z.number().int().min(1).max(6).default(4),
    }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { drainGenerationQueue } = await import("./editorial.server");
    return await drainGenerationQueue({
      maxTasks: data.maxTasks,
      concurrency: data.concurrency,
      timeBudgetMs: 50_000,
    });
  });

export const runBackfillPublish = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({
      targetCount: z.number().int().min(1).max(300).default(120),
      lookbackDays: z.number().int().min(7).max(180).default(60),
      perDay: z.number().int().min(1).max(10).default(2),
      minQuality: z.number().int().min(0).max(100).default(55),
    }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { backfillPublish } = await import("./editorial.server");
    return await backfillPublish(data);
  });

/* -------- Cover image backfill -------- */

export const runCoverBackfill = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ limit: z.number().int().min(1).max(50).default(8) }).parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { backfillCoverImages } = await import("./cover-image.server");
    return await backfillCoverImages({ limit: data.limit, timeBudgetMs: 90_000 });
  });

export const getMissingCoverCount = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { countMissingCovers } = await import("./cover-image.server");
    return { missing: await countMissingCovers() };
  });

export const generateCoverForArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ articleId: z.string().uuid() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { ensureCoverForArticle } = await import("./cover-image.server");
    const result = await ensureCoverForArticle(data.articleId);
    if (!result) throw new Error("Article not found");
    return result;
  });

/**
 * Re-roll covers whose image_type is over-represented across recently
 * generated covers. Forces a fresh art-director pass + regen for each.
 */
export const rerollOverusedCovers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        limit: z.number().int().min(1).max(20).default(5),
        /** Any image_type taking more than this share of recent covers is "over quota". */
        thresholdPct: z.number().min(0.1).max(1).default(0.25),
        /** Window of recent covers to evaluate. */
        recentWindow: z.number().int().min(6).max(200).default(40),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);

    // Find over-quota image_type values across the most recent N art-directed
    // covers, then re-roll the oldest published articles using those types.
    const { data: recent } = await context.supabase
      .from("articles")
      .select("id, title, cover_art_direction, updated_at")
      .not("cover_art_direction", "is", null)
      .order("updated_at", { ascending: false })
      .limit(data.recentWindow);

    const counts: Record<string, number> = {};
    for (const r of recent ?? []) {
      const t = (r as any).cover_art_direction?.image_type as string | undefined;
      if (t) counts[t] = (counts[t] ?? 0) + 1;
    }
    const total = (recent ?? []).length || 1;
    const overUsed = Object.entries(counts)
      .filter(([, n]) => n / total > data.thresholdPct)
      .map(([t]) => t);

    if (overUsed.length === 0) {
      return { rerolled: 0, failed: 0, overUsed: [], errors: [] as string[] };
    }

    // Oldest first so we work through the visible archive.
    const { data: targets } = await context.supabase
      .from("articles")
      .select("id, title")
      .in(
        "id",
        (recent ?? [])
          .filter((r: any) => overUsed.includes(r.cover_art_direction?.image_type))
          .map((r: any) => r.id),
      )
      .order("updated_at", { ascending: true })
      .limit(data.limit);

    const { ensureCoverForArticle } = await import("./cover-image.server");
    const errors: string[] = [];
    let rerolled = 0;
    let failed = 0;
    const started = Date.now();
    const BUDGET_MS = 80_000;
    for (const t of targets ?? []) {
      if (Date.now() - started > BUDGET_MS) break;
      try {
        await ensureCoverForArticle((t as any).id, { force: true });
        rerolled++;
      } catch (err) {
        failed++;
        errors.push(`${((t as any).title ?? "").slice(0, 60)}: ${(err as Error).message}`);
      }
    }

    return { rerolled, failed, overUsed, errors: errors.slice(0, 5) };
  });

/* -------- Embeddings backfill -------- */

export const getEmbeddingBackfillStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { getEmbeddingsBackfillCounts } = await import("./embeddings-backfill.server");
    return await getEmbeddingsBackfillCounts();
  });

export const runEmbeddingBackfill = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        batchSize: z.number().int().min(1).max(100).default(25),
        timeBudgetMs: z.number().int().min(1000).max(50000).default(25000),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { runEmbeddingsBackfillBatch } = await import("./embeddings-backfill.server");
    return await runEmbeddingsBackfillBatch(data);
  });

/* -------- Manual (URL / text) draft generation for /admin/new -------- */

export const adminGenerateDraftFromInput = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        mode: z.enum(["url", "text"]),
        url: z.string().url().optional().nullable(),
        text: z.string().max(30_000).optional().nullable(),
        hint: z.string().max(600).optional().nullable(),
        categorySlug: z.string().max(80).optional().nullable(),
      })
      .refine(
        (v) => (v.mode === "url" ? !!v.url : (v.text ?? "").trim().length >= 200),
        { message: "Provide a URL or at least 200 characters of source text." },
      )
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await ensureAdmin(context.supabase, context.userId);
    const { generateDraftFromInput } = await import("./editorial.server");
    try {
      return await generateDraftFromInput(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const m = msg.match(/schema validation failed after repair: (.*)$/);
      if (!m) throw err;
      const labels: Record<string, string> = {
        content_md: "article body too short",
        title: "headline length",
        excerpt: "summary length",
        seo_title: "SEO title too long",
        seo_description: "SEO description too long",
        key_takeaways: "key takeaways",
        faq: "FAQ",
        external_citations: "no usable source links",
      };
      const fields = Array.from(new Set(m[1].split("; ").map((e) => e.split(/[.:]/)[0])));
      const nice = fields.map((f) => labels[f] ?? f).join(", ");
      throw new Error(`The AI draft didn't pass our checks (${nice}). Please try again.`);
    }
  });



